<template>
  <div
    v-if="isFilterSidebarOpen"
    class="fixed inset-0 z-10 h-screen w-screen overflow-hidden bg-neutral-500/50 transition-opacity"
  />
  <transition
    enter-active-class="transition duration-500 ease-in-out"
    leave-active-class="transition duration-500 ease-in-out"
    enter-from-class="translate-x-full"
    enter-to-class="translate-x-0"
    leave-from-class="translate-x-0"
    leave-to-class="translate-x-full"
  >
    <SfDrawer
      ref="filterSidebar"
      v-model="isFilterSidebarOpen"
      :placement="'right'"
      class="z-20 flex w-[370px] flex-col bg-white"
    >
      <header class="flex items-center justify-between bg-primary-700 p-4">
        <div class="flex items-center text-white">
          {{ $t('Filter & Sort') }}
        </div>
        <SfButton
          square
          variant="tertiary"
          class="text-white"
          @click="toggleFilterSidebar"
        >
          <SfIconClose />
        </SfButton>
      </header>
      <div
        class="overflow-y-auto p-4"
      >
        <div
          v-if="showNew"
          class="flex justify-between border-t border-neutral-100 py-3"
        >
          <label for="new">
            {{ $t('New') }}
          </label>
          <SfSwitch
            id="new"
            :model-value="selectedFilters?.recent"
            @change="selectNewItems"
          />
        </div>
        <div
          v-if="showSale"
          class="flex justify-between border-t border-neutral-100 py-3"
        >
          <label for="sale">
            {{ $t('Sale') }}
          </label>
          <SfSwitch
            id="sale"
            :model-value="selectedFilters?.sale"
            @change="selectSaleItems"
          />
        </div>
        <div
          v-if="showMykik"
          class="flex justify-between border-t border-neutral-100 py-3"
        >
          <label for="mykik">
            {{ $t('myKiK') }}
          </label>
          <SfSwitch
            id="mykik"
            :model-value="selectedFilters?.mykik"
            @change="selectMyKiKItems"
          />
        </div>

        <div
          v-if="showOekotex"
          class="flex justify-between border-y border-neutral-100 py-3"
        >
          <label
            for="oekotex"
            class="flex gap-2"
          >
            {{ $t('oekotex') }}
          </label>
          <SfSwitch
            id="oekotex"
            :model-value="selectedFilters?.oekotex"
            @change="selectOekotexItems"
          />
        </div>
        <KikAccordion
          v-if="hasPriceRange"
          id="Price"
          :index="1"
          title="Price"
        >
          <template #subTitle>
            <span
              v-if="isPriceRangeSelected"
              class="pl-4 text-sm text-primary-700"
            >
              {{ $n(selectedFilters.price?.min ?? 0, 'currency') }} - {{ $n(selectedFilters.price?.max ?? 0, 'currency') }}
            </span>
          </template>
          <div class="mt-5">
            <KikSlider
              v-if="selectedFilters?.price"
              :key="`${selectedFilters.price?.min}-${selectedFilters.price?.max}`"
              v-model:min-value="selectedFilters.price.min"
              v-model:max-value="selectedFilters.price.max"
              :step="0.5"
              :min="initialPriceRange.min - 0.01"
              :max="initialPriceRange.max"
              :debounce="300"
              @on-apply-filter="updatePriceRangeWithSlider"
            />
          </div>
        </KikAccordion>
        <KikAccordion
          id="target_group"
          :index="0"
          title="target_group"
        >
          <template #subTitle>
            <span
              v-if="selectedFilters?.targetgroup"
              class="pl-4 text-sm text-primary-700"
            >
              {{ $t(`target_group_${selectedFilters.targetgroup}`) }}
            </span>
          </template>
          <label
            v-for="(option, i) in targetGroupOptions"
            :key="`targetgroup-${option}-${i}`"
            class="mr-6 mt-3 flex cursor-pointer items-center gap-2 rounded"
          >
            <SfRadio
              name="targetgroup"
              :value="option"
              :model-value="selectedFilters?.targetgroup"
              @input="selectTargetGroupOption"
              @keydown.space.prevent="selectTargetGroupOption"
              @keydown.enter.prevent="selectTargetGroupOption"
            />
            <span class="font-body text-sm font-normal leading-5">{{ $t(`target_group_${option}`) }}</span>
          </label>
        </KikAccordion>
        <KikAccordion
          v-if="filters?.search_color"
          id="search_color"
          :index="2"
          title="search_color"
        >
          <template #subTitle>
            <span
              v-if="getSelectedFiltersNames({ id: 'search_color' })"
              class="pl-4 text-sm text-primary-700"
            >
              {{ getSelectedFiltersNames({ id: 'search_color' }) }}
            </span>
          </template>
          <div class="mt-3 flex flex-wrap gap-4">
            <SfThumbnail
              v-for="(option, index) in filters.search_color.options"
              :key="`search-color-${option.label}`"
              size="lg"
              class="cursor-pointer border"
              :title="$t(option.id)"
              :class="{ 'border-4 border-white ring-1 ring-negative-700': isFilterSelected('search_color', option.label) }"
              :style="{ background: `${option.code}` }"
              :tabindex="index + 1"
              @click="onSelectFilter('search_color', option.label)"
              @keydown.space.prevent="onSelectFilter('search_color', option.label)"
              @keydown.enter.prevent="onSelectFilter('search_color', option.label)"
            />
          </div>
        </KikAccordion>
        <KikAccordion
          v-for="(facet, i) in availableFilters"
          :id="facet.id"
          :key="facet.id"
          :index="i + 2"
          :title="facet.id"
          :class="[(facet.options as string).length ? 'block': 'hidden']"
        >
          <template #subTitle>
            <span
              v-if="getSelectedFiltersNames(facet)"
              class="pl-4 text-sm capitalize text-primary-700"
            >
              {{ getSelectedFiltersNames(facet) }}
            </span>
          </template>
          <label
            v-for="option in facet.options"
            :key="`${facet.id}-${option}`"
            class="mr-6 mt-3 flex cursor-pointer items-center gap-2 rounded"
          >
            <SfCheckbox
              name="sort"
              :model-value="isFilterSelected(facet.id, option)"
              :title="option"
              @change="onSelectFilter(facet.id, option)"
              @keydown.enter.prevent="onSelectFilter(facet.id, option)"
              @keydown.space.prevent="onSelectFilter(facet.id, option)"
            />
            <span class="font-body text-sm font-normal capitalize leading-5">{{ option }}</span>
          </label>
        </KikAccordion>
      </div>
      <div class="sticky bottom-0 mt-auto w-full bg-white p-4 shadow-inner">
        <SfButton
          class="w-full uppercase"
          :aria-label="$t('Show results')"
          :disabled="loading"
          variant="primary"
          @click="handleShowResults"
        >
          <SfLoaderCircular v-if="loading" />
          <span>{{ selectedFiltersTransformed && !loading ? productCount : '' }} {{ $t('Show results') }}</span>
        </SfButton>

        <SfButton
          v-if="showClearFilters"
          class="mt-4 w-full uppercase !text-neutral-900 hover:!bg-neutral-100"
          :aria-label="$t('Clear all filters')"
          variant="secondary"
          @click="onResetFilter"
        >
          {{ $t('Clear all filters') }}
        </SfButton>
      </div>
    </SfDrawer>
  </transition>
</template>

<script lang="ts" setup>
import {
  SfIconClose,
  SfDrawer,
  SfButton,
  SfSwitch,
  SfCheckbox,
  SfThumbnail,
  SfRadio,
  SfLoaderCircular, useTrapFocus,
} from '@storefront-ui/vue';
import { onClickOutside, useDebounceFn } from '@vueuse/core';
import { TargetGroupType } from '~/components/Product/types/product';
import { useProduct } from '~/composables/custom/useProduct/useProduct';
import { useSSR } from '~/composables/useSSR/useSSR';
import { useFilters, type ProductFacets, type SelectedFilters } from '~/composables/custom/useFilters';
import { useFocusStore } from '~/store/focus';

interface Props {
  pageId: string;
  categories?: string[];
  products?: string[];
  productCount?: number;
}

const { setPreviousElement } = useFocusStore();

const props = withDefaults(defineProps<Props>(), {
  categories: () => ([]),
  products: () => ([]),
  productCount: undefined,
});

const emit = defineEmits(['on-filter-selected', 'on-apply-filter', 'close-sidebar']);

const {
  changeFilters,
  calculateFilters,
  priceFilter: initialPriceRange,
  selectedFiltersTransformed,
  resetFilters,
  selectedFilters,
  setSelectedFilter,
  filterList: filters,
  applyInitialPriceRange,
} = useFilters();

watch(() => props.categories, async (newCategories) => {
  if (newCategories.length) {
    await applyInitialPriceRange(newCategories, props.products);
  }
});

const { loading } = useProduct();

const { onSSR } = useSSR();

const { toggleFilterSidebar, isFilterSidebarOpen, closeFilterSidebar } = useUiState();
const { $i18n } = useNuxtApp();

const filterSidebar = ref();
useTrapFocus(filterSidebar, { activeState: isFilterSidebarOpen });

onClickOutside(filterSidebar, () => {
  closeFilterSidebar();
});

const getSelectedFiltersNames = ({ id: facetId }: { id: string }) => {
  if (facetId === 'search_color') {
    return selectedFilters.value?.[facetId].map((selected) => {
      const id = filters.value?.search_color?.options?.find(({ label }) => label === selected)?.id;
      return id ? $i18n.t(id) : selected;
    }).join(', ');
  }

  return (selectedFilters.value?.[facetId as keyof SelectedFilters] as string[] | undefined)?.join(', ') ?? '';
};

const isFilterSelected = (facetId: string, option: string) => (selectedFilters.value[facetId as keyof SelectedFilters] as string[] | undefined)?.some((value: any) => value === option);

const onSelectFilter = (facetId: string, option: string) => {
  setPreviousElement(facetId);
  let newSelectedList = selectedFilters.value[facetId as keyof SelectedFilters] as string[] || [];

  if (newSelectedList?.some((facetName: string) => facetName === option)) {
    newSelectedList = newSelectedList.filter((name: string) => name !== option) || [];
  }
  else {
    newSelectedList.push(option);
  }

  setFilter(facetId, newSelectedList);
};

const handleShowResults = () => {
  toggleFilterSidebar();
  emit('close-sidebar');
};

const setFilter = (key: string, value: any) => {
  setSelectedFilter(key, value);
  if (key === 'search_color') {
    changeFilters(selectedFilters.value);
    emit('on-apply-filter', false);
  }
  else {
    debounceApplyFilter();
  }
};

const selectNewItems = () => {
  setFilter('recent', !selectedFilters.value?.recent);
};

const selectSaleItems = () => {
  setFilter('sale', !selectedFilters.value?.sale);
};

const selectMyKiKItems = () => {
  setFilter('mykik', !selectedFilters.value?.mykik);
};

const selectOekotexItems = () => {
  setFilter('oekotex', !selectedFilters.value?.oekotex);
};

const debounceApplyFilter = useDebounceFn(() => {
  changeFilters(selectedFilters.value);
  emit('on-apply-filter');
}, 1000);

const selectTargetGroupOption = (option: Event) => {
  setPreviousElement('target_group');

  setFilter('targetgroup', (option.target as HTMLInputElement)?.value);
};

const updatePriceRangeWithSlider = async (minNum: number, maxNum: number) => {
  if (minNum === initialPriceRange.value.min && maxNum === initialPriceRange.value.max) {
    await onResetFilter();
    return;
  }
  setPreviousElement('Price');

  setFilter('price', {
    min: minNum,
    max: maxNum,
  });
};

const onResetFilter = async () => {
  await resetFilters(props.categories, props.products);
  await debounceApplyFilter();
};

onSSR(async () => {
  await calculateFilters(props.categories);
});

const isPriceRangeSelected = computed(() => (selectedFilters.value.price?.min ?? 0) > initialPriceRange.value?.min || (selectedFilters.value.price?.max ?? 1000000) < initialPriceRange.value?.max);
const showClearFilters = computed(() => selectedFiltersTransformed.value);
const availableFilters = computed(() => (
  Object.keys(filters.value ?? {}).filter(key => key !== 'search_color' && key !== 'price').map(key => ({ id: key, options: filters.value[key as keyof ProductFacets] }))
));
const targetGroupOptions = Object.values(TargetGroupType);
const hasPriceRange = computed(() => initialPriceRange.value?.min >= 0 && initialPriceRange.value?.max);
const showNew = computed(() => !filters.value?.hide?.recent);
const showSale = computed(() => !filters.value?.hide?.sale);
const showMykik = computed(() => !filters.value?.hide?.mykik);
const showOekotex = computed(() => !filters.value?.hide?.oekotex);
</script>
